// ─────────────────────────────────────────────────────────────────────────────
// "نسيت كلمة المرور؟" — asking for a reset link.
//
// ── THE SCREEN SAYS THE SAME THING EVERY TIME ───────────────────────────────
//
// There is one success state and it is shown whether or not the address
// belongs to anybody: no match, a terminated employee, an address two people
// share, or over the rate limit all land here. The server returns `{ ok: true }`
// for every one of them and holds a timing floor so the fast rejections cannot
// be told apart from the slow success either.
//
// This is worth defending against the obvious objection, which is that it is
// unhelpful. A form that says "this email is not registered" is a free
// membership check: type an address, learn whether that person works at Alba.
// For a clinic whose staff list is not public, that is a real disclosure, and
// the person genuinely mistyping their own address is helped by the note about
// checking spam and asking Ali — not by confirming which addresses exist.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type FormEvent, type ReactElement } from "react";
import { trpc } from "../../api";
import { useI18n } from "../../i18n";
import { ErrorNotice } from "../../ui";

export function ForgotPasswordPage({ onBack }: { readonly onBack: () => void }): ReactElement {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const request = trpc.auth.requestPasswordReset.useMutation();

  function submit(event: FormEvent): void {
    event.preventDefault();
    request.mutate({ email });
  }

  if (request.isSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 style={{ textAlign: "center", color: "var(--brand)" }}>{t("auth.forgotTitle")}</h1>
          <div className="alert alert-info">{t("auth.forgotSent")}</div>
          <p className="muted">{t("auth.forgotSentNote")}</p>
          <button type="button" className="btn" style={{ width: "100%" }} onClick={onBack}>
            {t("auth.backToSignIn")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={submit}>
        <h1 style={{ textAlign: "center", color: "var(--brand)" }}>{t("auth.forgotTitle")}</h1>
        <p className="muted">{t("auth.forgotIntro")}</p>

        {/* Only a transport failure can reach this. A refusal for a business
            reason is unreachable by design — the procedure has none. */}
        <ErrorNotice error={request.error} />

        <div className="field">
          <label htmlFor="reset-email">{t("common.email")}</label>
          <input
            id="reset-email" name="email" type="email" autoComplete="email" autoFocus required
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={request.isPending}>
          {request.isPending ? t("common.loading") : t("auth.forgotSubmit")}
        </button>

        <button type="button" className="btn btn-sm" style={{ width: "100%", marginTop: "var(--space-3)" }} onClick={onBack}>
          {t("auth.backToSignIn")}
        </button>
      </form>
    </div>
  );
}
